import chartTheme from "@components/Charts/chartTheme";
import TitledChartSkeleton from "@components/TitledChartSkeleton";
import { ClientContext } from "@context/Client/ClientContext";
import { BarChart, BarChartTooltip } from "@dive.tech/ui-graphs";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useClientModelVariables, useModelScenario } from "@services/api";
import { ModelScenarioInput } from "@services/api/types";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface AggregatedScenarioInput {
  name: string;
  value: number;
}

const aggregateScenarioInputs = (
  inputs: ModelScenarioInput[],
  externalVarNames: string[]
): AggregatedScenarioInput[] => {
  const scenarioInputs = inputs.filter(
    (input) => !externalVarNames.includes(input.name)
  );
  const scenarioMediasRaw = scenarioInputs.map((input) => input.name);
  const scenarioMedias = new Set(scenarioMediasRaw);
  const scenarioMediasNames = Array.from(scenarioMedias);
  return scenarioMediasNames.map((media) => {
    const mediaSpend = scenarioInputs
      .filter((input) => input.name === media)
      .reduce((curr, acc) => curr + acc.value, 0);
    return {
      name: media,
      value: mediaSpend
    };
  });
};

export interface MediaSpendVariationChartProps {
  scenario1Id: string;
  scenario2Id: string;
}
const MediaSpendVariationChart = ({
  scenario1Id,
  scenario2Id
}: MediaSpendVariationChartProps) => {
  const { t } = useTranslation();
  const { currentClient } = useContext(ClientContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mdValue = theme.breakpoints.values.md; // 900
  // Ref and state for dynamic width
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(mdValue);

  const { data: scenario1, isFetching: isLoadingScenario1 } = useModelScenario(
    scenario1Id ?? ""
  );
  const { data: scenario2, isFetching: isLoadingScenario2 } = useModelScenario(
    scenario2Id ?? ""
  );

  const { data: modelVars, isFetching: isLoadingExtVars } =
    useClientModelVariables(currentClient.model.id);

  const isLoading = useMemo(
    () => isLoadingScenario1 || isLoadingScenario2 || isLoadingExtVars,
    [isLoadingScenario1, isLoadingScenario2, isLoadingExtVars]
  );

  const externalVarNames: string[] = useMemo(() => {
    return (modelVars || [])
      .filter((modelVariable) => modelVariable.type === "external")
      .map((modelVar) => modelVar.name);
  }, [modelVars]);

  const scenario1Name = useMemo(() => {
    return scenario1?.scenario.name || "Scenario 1";
  }, [scenario1]);

  const scenario2Name = useMemo(() => {
    return scenario2?.scenario.name || "Scenario 2";
  }, [scenario2]);

  const barData = useMemo(() => {
    if (isLoading) {
      return { data: [], colors: [], max: 0 };
    }
    const scenario1Medias = aggregateScenarioInputs(
      scenario1?.inputs || [],
      externalVarNames
    );
    const scenario2Medias = aggregateScenarioInputs(
      scenario2?.inputs || [],
      externalVarNames
    );

    const mediaNamesRaw = [
      ...scenario1Medias.map((media) => media.name),
      ...scenario2Medias.map((media) => media.name)
    ];
    const mediaNames = new Set(mediaNamesRaw);
    const mediaData = Array.from(mediaNames).map((media) => {
      const scenario1Media = scenario1Medias.find(
        (mediaInput) => mediaInput.name === media
      );
      const scenario2Media = scenario2Medias.find(
        (mediaInput) => mediaInput.name === media
      );
      const diff = (scenario1Media?.value || 0) - (scenario2Media?.value || 0);
      return {
        name: media,
        variation: diff,
        color:
          diff > 0
            ? theme.palette.customColors.positiveVariationColor
            : theme.palette.customColors.negativeVariationColor
      };
    });
    return {
      data: mediaData.map((media) => ({
        name: media.name,
        variation: media.variation
      })),
      colors: mediaData.map((media) => media.color),
      max: Math.max(...mediaData.map((media) => media.variation)) * 1.1
    };
  }, [scenario1, scenario2, externalVarNames, theme, isLoading]);

  const barTickRotation = useMemo(() => {
    let rotation = 0;
    if (isMobile) {
      rotation = -25;
    }
    if (barData.data.length > 6) {
      rotation -= 20;
    }
    return rotation;
  }, [barData.data, isMobile]);

  // Measure container width on mount and resize
  useEffect(() => {
    const updateWidth = () => {
      if (chartContainerRef.current) {
        const width = chartContainerRef.current.offsetWidth;
        setChartWidth(width > 0 ? width : 1000);
      }
    };

    updateWidth();

    // Use ResizeObserver for better performance
    const resizeObserver = new ResizeObserver(updateWidth);
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <React.Fragment>
      {isLoading ? (
        <TitledChartSkeleton />
      ) : (
        <Box className="w-full">
          <Typography variant="h6" component="div" className="pt-4 pl-6 pb-4">
            {t("simulator.benchmark.media_spend_variation", {
              sc1Name: scenario1Name,
              sc2Name: scenario2Name
            })}
          </Typography>
          <Box sx={{ height: "500px" }} ref={chartContainerRef}>
            {barData && (
              <BarChart
                key={`${scenario1Id}-${scenario2Id}`}
                data={barData.data}
                responsiveBarProps={{
                  theme: chartTheme,
                  tooltip: (props: any) =>
                    BarChartTooltip({
                      color: props.color,
                      label: props.label,
                      value: props.value,
                      id: props.id,
                      index: props.index,
                      indexValue: props.indexValue,
                      data: props.data,
                      withSerieId: false,
                      yAxis: { yType: "number", yDecimals: 0 },
                      formattedValue: props.formattedValue,
                      hidden: props.hidden
                    }),
                  layers: ["grid", "markers", "axes", "legends", "bars"],
                  enableGridX: false,
                  enableLabel: false,
                  groupMode: "grouped",
                  indexScale: { type: "band", round: true },
                  indexBy: "name",
                  colorBy: "indexValue",
                  keys: ["variation"],
                  colors: barData.colors,
                  valueScale: {
                    type: "linear",
                    min: "auto",
                    max: barData.max
                  },
                  margin: {
                    top: 20,
                    left: isMobile ? 60 : 100,
                    right: 10,
                    bottom: isMobile ? 150 : 100
                  },
                  axisLeft: {
                    tickSize: isMobile ? 5 : 10,
                    tickRotation: isMobile ? -45 : 0,
                    legendOffset: isMobile ? -50 : -80,
                    legendPosition: "middle",
                    legend: t("simulator.benchmark.spend_difference")
                    //format: (value) => `${Number(value) / 1_000}k`
                  },
                  axisBottom: {
                    legend: t("simulator.benchmark.channel"),
                    legendPosition: "middle",
                    legendOffset: isMobile ? 120 : 70,
                    tickRotation: barTickRotation
                  },
                  legends: [],
                  width: chartWidth,
                  height: 500
                }}
              />
            )}
          </Box>
        </Box>
      )}
    </React.Fragment>
  );
};

export default MediaSpendVariationChart;
